// src/pages/Compliance.jsx
// Compliance (3.43.0): the Security & compliance report for one client and
// one period, as a PDF summary for auditors and an Excel file with every
// detail. Super Admins pick any client; people whose role has "Download
// their client's Security & compliance report" (Roles & Access, off for
// every role until ticked) get their own client's. The page shows the key
// figures first; the downloads are built from the same data (backend
// lib/complianceReport.js and complianceDocs.js) and recorded in the
// Activity log. Never emailed.
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { API_BASE } from '../firebase/config';
import { PAGE_TITLE, SECTION_SUB } from '../theme/tabs';
import { timeZone } from '../utils/timeZone';
import Loader from '../components/Loader';
import Emo from '../components/Emo';

const ymd = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function presetRange(id) {
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: timeZone() }));
  const y = now.getFullYear(), m = now.getMonth();
  if (id === 'this-month') return [ymd(new Date(y, m, 1)), ymd(now)];
  if (id === 'last-3') return [ymd(new Date(y, m - 3, 1)), ymd(new Date(y, m, 0))];
  if (id === 'this-year') return [ymd(new Date(y, 0, 1)), ymd(now)];
  return [ymd(new Date(y, m - 1, 1)), ymd(new Date(y, m, 0))];   // last month
}
const PRESETS = [['last-month', 'Last month'], ['this-month', 'This month so far'], ['last-3', 'Last 3 months'], ['this-year', 'This year so far'], ['custom', 'Custom']];

export default function Compliance() {
  const { getToken, effectiveInfo, userInfo } = useAuth();
  const { notify } = useNotify();
  const superView = userInfo?.role === 'super_admin' || effectiveInfo?.level === 'super_admin';
  const clients = useMemo(() => (effectiveInfo?.accounts || []), [effectiveInfo]);
  const picked = effectiveInfo?.accountId && effectiveInfo.accountId !== 'all' ? effectiveInfo.accountId : '';
  const [account, setAccount] = useState(picked || '');
  useEffect(() => { if (picked) setAccount(picked); }, [picked]);
  const [preset, setPreset] = useState('last-month');
  const [range, setRange] = useState(() => presetRange('last-month'));
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const pickPreset = id => { setPreset(id); if (id !== 'custom') setRange(presetRange(id)); };
  const qs = `account=${encodeURIComponent(account)}&from=${range[0]}&to=${range[1]}`;
  const ready = (!superView || account) && range[0] && range[1] && range[0] <= range[1];

  useEffect(() => {
    if (!ready) { setData(null); return undefined; }
    let live = true;
    setData(null); setError('');
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/portal/compliance/summary?${qs}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || "Couldn't build the report.");
        if (live) setData(j);
      } catch (e) { if (live) setError(e.message); }
    })();
    return () => { live = false; };
  }, [qs, ready, getToken]);

  async function download(format) {
    setBusy(format);
    try {
      const r = await fetch(`${API_BASE}/portal/compliance/download?${qs}&format=${format}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || "Couldn't build the report."); }
      const blob = await r.blob();
      const m = (r.headers.get('Content-Disposition') || '').match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = m ? m[1] : `Compliance report.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { notify({ tone: 'error', title: 'Download failed', detail: e.message }); }
    setBusy('');
  }

  const s = data?.summary;
  const cards = s ? [
    ['👥', 'People with access', s.people, s.removed ? `${s.removed} removed` : ''],
    ['🔐', 'Two-factor on', `${s.twoFactorOn}/${s.people}`, s.twoFactorOff ? `${s.twoFactorOff} still to set up` : 'everyone', s.twoFactorOff ? 'warn' : 'good'],
    ['🔑', 'Sign-ins', s.signIns, `by ${s.peopleSignedIn} ${s.peopleSignedIn === 1 ? 'person' : 'people'}`],
    // 3.47.4: "Support", not "Super Admin": what clients know us as
    ['👁️', 'Support View as', s.viewAsSessions, data.meta.forClient ? 'visits by Fax Portal support' : s.viewAsChanges ? `${s.viewAsChanges} change${s.viewAsChanges === 1 ? '' : 's'} made while viewing` : 'no changes made'],
    ['🛡️', 'Changes to access', s.accessChanges, 'people, roles, invites, resets'],
    ['⚙️', 'Settings changes', s.settingsChanges, ''],
    ['📤', 'Faxes sent', s.sent, `${s.delivered} delivered · ${s.failed} failed`],
    // 3.48.3: emailing is only known for faxes since it was recorded
    ['📥', 'Faxes received', s.received, s.emailRecorded ? `${s.emailedOk} of ${s.emailRecorded} emailed${data.meta.began?.incomingEmail ? ` since ${data.meta.began.incomingEmail}` : ''}` : 'emailing not recorded yet in this period'],
    ['🗑️', 'Moved to Trash', s.deletedSent + s.deletedReceived, `${s.faxActions} fax views, downloads and more`],
  ] : [];

  return (
    <div>
      <h2 style={PAGE_TITLE} className="cp-title"><Emo e="🔐" /> Compliance</h2>
      {/* 3.47.4: worded for whoever reads it: Super Admins pick a client, a client's people get their own */}
      <p style={{ ...SECTION_SUB, marginBottom: 14 }}>
        {superView
          ? <>The Security &amp; compliance report for one client and one period: who has access and how they sign in, changes to access and settings,
            visits by Fax Portal support (View as), what people did with faxes, data retention and the safeguards in place.</>
          : <>The Security &amp; compliance report for your company, for the period you pick: who has access and how they sign in, changes to access and settings,
            visits by Fax Portal support (View as), what your people did with faxes, data retention and the safeguards in place.</>}
        {' '}Download it as a PDF summary for auditors and an Excel file with every detail. It's a record for audits, not a certification, and it's never emailed.
      </p>

      <section style={S.panel}>
        <div style={S.controls}>
          {superView && (
            <label style={S.field}>Client
              <select value={account} onChange={e => setAccount(e.target.value)} style={S.input} aria-label="Client">
                <option value="">Pick a client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          )}
          <div style={S.field}>Period
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} data-pills>
              {PRESETS.map(([id, label]) => (
                <button key={id} type="button" onClick={() => pickPreset(id)} aria-pressed={preset === id} style={{ ...S.pill, ...(preset === id ? S.pillOn : {}) }}>{label}</button>
              ))}
            </div>
          </div>
          <label style={S.field}>From
            <input type="date" value={range[0]} disabled={preset !== 'custom'} onChange={e => setRange([e.target.value, range[1]])} style={S.input} />
          </label>
          <label style={S.field}>To
            <input type="date" value={range[1]} disabled={preset !== 'custom'} onChange={e => setRange([range[0], e.target.value])} style={S.input} />
          </label>
        </div>
        <div style={S.actions}>
          <button onClick={() => download('pdf')} disabled={!data || !!busy} style={{ ...S.primary, opacity: !data || busy ? 0.6 : 1 }}>
            <Emo e="📄" /> {busy === 'pdf' ? 'Building the PDF…' : 'Download PDF'}
          </button>
          <button onClick={() => download('xlsx')} disabled={!data || !!busy} style={{ ...S.dark, opacity: !data || busy ? 0.6 : 1 }}>
            <Emo e="📊" /> {busy === 'xlsx' ? 'Building the Excel file…' : 'Download Excel'}
          </button>
          {data && <span style={S.hint}>{data.meta.client} · {data.from} to {data.to}{data.meta.hipaa ? ' · HIPAA client' : ''}</span>}
        </div>
      </section>

      {!ready && superView && !account && <p style={S.hint}>Pick a client to see their report.</p>}
      {ready && !data && !error && <Loader label="Building the report… (it reads the period's activity, so it can take a little while)" />}
      {error && <div role="alert" style={S.error}>{error}</div>}
      {s && (
        <>
          <div style={S.grid}>
            {cards.map(([icon, label, value, sub, tone], i) => (
              <div key={label} className="ov-lift" style={{ ...S.card, animationDelay: `${i * 40}ms` }}>
                <div style={S.cardLabel}><Emo e={icon} /> {label}</div>
                <div style={{ ...S.cardValue, color: tone === 'warn' ? 'var(--t-e65100)' : tone === 'good' ? 'var(--t-2e7d32)' : 'var(--t-00695c)' }}>{value}</div>
                {sub && <div style={S.cardSub}>{sub}</div>}
              </div>
            ))}
          </div>
          {data.meta.coverage?.length > 0 && (
            <section style={{ ...S.panel, marginTop: 14 }}>
              <h3 style={S.h3}><Emo e="📅" /> What the records cover</h3>
              <ul style={S.list}>{data.meta.coverage.map(t => <li key={t}>{t}</li>)}</ul>
            </section>
          )}
          <section style={{ ...S.panel, marginTop: 14 }}>
            <h3 style={S.h3}><Emo e="🛡️" /> Safeguards in the report</h3>
            <ul style={S.list}>{data.safeguards.map(t => <li key={t}>{t}</li>)}</ul>
            <p style={S.hint}>{data.meta.testFaxText || `Test faxes in the period (the whole service): ${s.testsPassed} passed, ${s.testsFailed} failed.`}{data.meta.truncated ? ' The activity log was very large, so the first part of the period was read.' : ''}</p>
          </section>
        </>
      )}
    </div>
  );
}

const S = {
  panel: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 14, padding: '16px 18px', boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.04)' },
  controls: { display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' },
  field: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--t-546e7a)', textTransform: 'uppercase', letterSpacing: '0.4px' },
  input: { padding: '8px 10px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', fontSize: 14, fontFamily: 'inherit', background: 'var(--c-ffffff)', textTransform: 'none', letterSpacing: 0, fontWeight: 500, color: 'var(--t-263238)', minWidth: 160 },
  pill: { padding: '6px 14px', borderRadius: 18, border: '1.5px solid var(--c-cfd8dc)', background: 'var(--c-ffffff)', color: 'var(--t-546e7a)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'none', letterSpacing: 0 },
  pillOn: { background: 'var(--c-00695c)', border: '1.5px solid var(--c-00695c)', color: 'var(--t-ffffff)' },
  actions: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 16 },
  primary: { padding: '10px 18px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' },
  dark: { padding: '10px 18px', background: 'var(--c-37474f)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' },
  hint: { fontSize: 13, color: 'var(--t-78909c)', margin: '8px 0 0' },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginTop: 12 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginTop: 16 },
  card: { background: 'var(--c-ffffff)', border: '1px solid var(--b-c8e6c9)', borderRadius: 12, padding: '12px 14px', boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.06)' },
  cardLabel: { fontSize: 11.5, color: 'var(--t-78909c)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  cardValue: { fontSize: 26, fontWeight: 800, marginTop: 4 },
  cardSub: { fontSize: 12, color: 'var(--t-78909c)', marginTop: 2 },
  h3: { margin: '0 0 8px', color: 'var(--t-00695c)', fontSize: 16 },
  list: { margin: '0 0 6px', paddingLeft: 20, fontSize: 13.5, color: 'var(--t-37474f)', lineHeight: 1.7 },
};
